import {
    Integration,
    PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN
} from '@app/views/admin/application-settings/models/integration.model';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';
import {IntegrationTask} from '@app/views/admin/application-settings/models/integration-task.model';
import {DataAttribute} from '@app/views/admin/application-settings/models/data-attribute.model';
import {IntegrationTaskMap} from '@app/views/admin/application-settings/models/integration-task-map.model';
import {AttributeEntity} from '@app/core/model/integration-models';
import {getAllowedAttributes} from '@app/core/model/allowed-filtered-attributes';
import {ApplicationSettingsService} from '@app/views/admin/application-settings/application-settings.service';
import {DataSourceConfig} from '@app/views/admin/application-settings/models/data-source-config.model';

export class FilterRuleMetadata {

    static createFromIntegration(
        integration: Integration, includeTaskIdentifier: boolean = false, dataSourceConfig?: DataSourceConfig
    ): FilterRuleMetadata[] {
        const eduToneDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';
        const otherDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';

        const pageAvailableTasksList = ApplicationSettingsService.getPageAvailableTasksList(
            PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.filterRules, integration[otherDS].dataSourceType, dataSourceConfig);

        let result = <FilterRuleMetadata[]>[];
        if (integration.tasks) {
            integration.tasks
                .filter((task: IntegrationTask) => pageAvailableTasksList.includes(task[`${eduToneDS}Type`]))
                .forEach((task: IntegrationTask) => {
                    // get Mapped Edutone AttributeList
                    const attributes: DataAttribute[] =
                        task.mappings
                            .map((mapping: IntegrationTaskMap) => {
                                if (mapping[eduToneDS + 'Property'] && mapping.enabled) {
                                    return mapping[eduToneDS + 'Property'];
                                } else {
                                    return task[eduToneDS + 'AttributeList']
                                        .find((attr: DataAttribute) =>
                                            ((attr.propertyName === mapping[eduToneDS + 'PropertyName']) && (mapping.enabled)));
                                }
                            })
                            .filter((attr: DataAttribute, attrIndex: number, self: DataAttribute[]) =>
                                attr && self.indexOf(attr) === attrIndex);

                    const taskIdList = [task.taskId];
                    if (includeTaskIdentifier) {
                        taskIdList.unshift(task.sourceType + '-' + task.targetType);
                    }
                    result = FilterRuleMetadata.combineFilterRuleMetadata(
                        taskIdList, task[eduToneDS + 'Type'], attributes, result, task[eduToneDS + 'AttributeList']);
                });
        }

        return result;
    }

    static createFromAttributeEntityList(attrEntities: AttributeEntity[], integration: Integration): FilterRuleMetadata[] {
        let result = <FilterRuleMetadata[]>[];
        const otherDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
        const pageAvailableTasksList = ApplicationSettingsService.getPageAvailableTasksList(
            PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.filterRules, integration[otherDS].dataSourceType);

        attrEntities
            .filter((attrEntity: AttributeEntity) => pageAvailableTasksList.includes(attrEntity.sourceType))
            .forEach((attrEntity: AttributeEntity) => {
                const taskId = attrEntity.sourceType + '-' + attrEntity.targetType;
                let edutoneAttributesList = [];
                if (integration && integration.tasks) {
                    const currentTask = integration.tasks.find((task: IntegrationTask) => task.taskId === attrEntity.taskId);
                    const eduToneDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';
                    edutoneAttributesList = currentTask[eduToneDS + 'AttributeList'];
                }

                result = FilterRuleMetadata.combineFilterRuleMetadata(
                    [taskId], attrEntity.displayType, attrEntity.attributes, result, edutoneAttributesList);
            });

        return result;
    }

    // Add new FilterRuleMetadata to unique list with unique list of attributes
    static combineFilterRuleMetadata(
        taskIdList: string[], type: string, attributes: DataAttribute[], list: FilterRuleMetadata[], edutoneAttributesList: DataAttribute[]
    ): FilterRuleMetadata[] {
        const allowedAttributes = getAllowedAttributes(type, attributes)
            .filter((attr: DataAttribute) => attr.propertyName);

        const existingFilterRuleMetadata: FilterRuleMetadata =
            list.find((item: FilterRuleMetadata) => item.type === type);

        // Try to combine attributes for the same entity types
        if (existingFilterRuleMetadata) {
            existingFilterRuleMetadata.taskIdList = existingFilterRuleMetadata.taskIdList.concat(taskIdList);

            allowedAttributes.forEach((attr: DataAttribute) => {
                const attrExist = existingFilterRuleMetadata.attributes
                    .find((itemAttr: DataAttribute) => itemAttr.propertyName === attr.propertyName);
                if (!attrExist) {
                    existingFilterRuleMetadata.attributes.push(attr);
                }
            });

            existingFilterRuleMetadata.attributes.sort(DataAttribute.compareByPropertyName);
        } else {
            allowedAttributes.sort(DataAttribute.compareByPropertyName);

            list.push(new FilterRuleMetadata(taskIdList, type, allowedAttributes, edutoneAttributesList));
        }

        return list;
    }

    constructor(
        public taskIdList: string[] = <string[]>[],
        public type: string = '',
        public attributes: DataAttribute[] = <DataAttribute[]>[],
        public edutoneAttributesList: DataAttribute[] = <DataAttribute[]>[]) {
    }
}
